import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiMaximize2, FiX } from 'react-icons/fi';
import styles from './ArtworkImageGallery.module.css';

const ArtworkImageGallery = ({ 
  images = [], 
  title = 'Artwork',
  className = '',
  showThumbnails = true,
  autoPlay = false,
  autoPlayInterval = 5000
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  // Ensure we have at least one image
  const validImages = images.filter(img => img && img.image_url);
  const hasMultipleImages = validImages.length > 1;

  useEffect(() => {
    if (validImages.length > 0) {
      setCurrentIndex(0);
    }
  }, [validImages.length]);

  // Auto play functionality
  useEffect(() => {
    if (autoPlay && hasMultipleImages && !isFullscreen) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % validImages.length);
      }, autoPlayInterval);
      
      return () => clearInterval(interval);
    }
  }, [autoPlay, hasMultipleImages, isFullscreen, validImages.length, autoPlayInterval]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isFullscreen) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case 'Escape':
          e.preventDefault();
          setIsFullscreen(false);
          break;
        default:
          break;
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  const goToNext = () => {
    if (hasMultipleImages) {
      setCurrentIndex((prev) => (prev + 1) % validImages.length);
    }
  };

  const goToPrevious = () => {
    if (hasMultipleImages) {
      setCurrentIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
    }
  };

  const goToIndex = (index) => {
    if (index >= 0 && index < validImages.length) {
      setCurrentIndex(index);
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = (imageIndex) => {
    setImageErrors(prev => ({ ...prev, [imageIndex]: true }));
    setIsLoading(false);
  };

  const openFullscreen = () => {
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  if (validImages.length === 0) {
    return (
      <div className={`${styles.gallery} ${styles.empty} ${className}`}>
        <div className={styles.placeholder}>
          <span>No images available</span>
        </div>
      </div>
    );
  }

  const currentImage = validImages[currentIndex];

  return (
    <>
      <div className={`${styles.gallery} ${className}`}>
        {/* Main Image Display */}
        <div className={styles.mainImageContainer}>
          {isLoading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.spinner}></div>
            </div>
          )}
          
          <img
            src={currentImage.image_url}
            alt={currentImage.alt_text || `${title} - Image ${currentIndex + 1}`}
            className={styles.mainImage}
            onLoad={handleImageLoad}
            onError={() => handleImageError(currentIndex)}
            onClick={openFullscreen}
          />

          {/* Navigation Arrows */}
          {hasMultipleImages && (
            <>
              <button
                className={`${styles.navButton} ${styles.prevButton}`}
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                <FiChevronLeft />
              </button>
              <button
                className={`${styles.navButton} ${styles.nextButton}`}
                onClick={goToNext}
                aria-label="Next image"
              >
                <FiChevronRight />
              </button>
            </>
          )}

          {/* Fullscreen Button */}
          <button
            className={styles.fullscreenButton}
            onClick={openFullscreen}
            aria-label="View fullscreen"
          >
            <FiMaximize2 />
          </button>

          {/* Image Counter */}
          {hasMultipleImages && (
            <div className={styles.imageCounter}>
              {currentIndex + 1} / {validImages.length}
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {showThumbnails && hasMultipleImages && (
          <div className={styles.thumbnailContainer}>
            <div className={styles.thumbnailGrid}>
              {validImages.map((image, index) => (
                <button
                  key={image.id || index}
                  className={`${styles.thumbnail} ${
                    index === currentIndex ? styles.active : ''
                  }`}
                  onClick={() => goToIndex(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <img
                    src={image.image_url}
                    alt={image.alt_text || `Thumbnail ${index + 1}`}
                    onError={() => handleImageError(index)}
                  />
                  {image.is_primary && (
                    <div className={styles.primaryBadge}>Primary</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dots Navigation (alternative to thumbnails for mobile) */}
        {!showThumbnails && hasMultipleImages && (
          <div className={styles.dotsContainer}>
            {validImages.map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${
                  index === currentIndex ? styles.active : ''
                }`}
                onClick={() => goToIndex(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className={styles.fullscreenOverlay}>
          <div className={styles.fullscreenContainer}>
            {/* Close Button */}
            <button
              className={styles.fullscreenClose}
              onClick={closeFullscreen}
              aria-label="Close fullscreen"
            >
              <FiX />
            </button>

            {/* Fullscreen Image */}
            <div className={styles.fullscreenImageContainer}>
              <img
                src={currentImage.image_url}
                alt={currentImage.alt_text || `${title} - Image ${currentIndex + 1}`}
                className={styles.fullscreenImage}
              />

              {/* Fullscreen Navigation */}
              {hasMultipleImages && (
                <>
                  <button
                    className={`${styles.fullscreenNav} ${styles.fullscreenPrev}`}
                    onClick={goToPrevious}
                    aria-label="Previous image"
                  >
                    <FiChevronLeft />
                  </button>
                  <button
                    className={`${styles.fullscreenNav} ${styles.fullscreenNext}`}
                    onClick={goToNext}
                    aria-label="Next image"
                  >
                    <FiChevronRight />
                  </button>
                </>
              )}
            </div>

            {/* Fullscreen Info */}
            <div className={styles.fullscreenInfo}>
              <div className={styles.fullscreenTitle}>
                {title} {hasMultipleImages && `(${currentIndex + 1}/${validImages.length})`}
              </div>
              
              {/* Fullscreen Thumbnails */}
              {hasMultipleImages && (
                <div className={styles.fullscreenThumbnails}>
                  {validImages.map((image, index) => (
                    <button
                      key={image.id || index}
                      className={`${styles.fullscreenThumbnail} ${
                        index === currentIndex ? styles.active : ''
                      }`}
                      onClick={() => goToIndex(index)}
                    >
                      <img
                        src={image.image_url}
                        alt={`Thumbnail ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ArtworkImageGallery; 